import {
	ErrorModal,
	HelmetDexteel,
	useContextMenuMESF,
} from "@dexteel/mesf-core";
import {
	DragLayerMonitorProps,
	DropOptions,
	isAncestor,
	NodeModel,
	Tree,
} from "@minoru/react-dnd-treeview";
import { Alert, Box, CircularProgress, Snackbar } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { mesfColors } from "@setup/ag-grid/mesf-theme";
import {
	MouseEvent as ReactMouseEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useShelvesContext } from "../context/ShelvesContext";
import { useShelvesOptionsFunctions } from "../hooks/useShelvesOptionsFunctions";
import { FileProperties } from "../models/FileProperties";
import { Shelf } from "../models/Shelf";
import { CustomNode } from "./CustomNode";
import { moveShelf } from "../repositories/ShelvesRepository";
import { CustomDragPreview, type DropFeedback } from "./drag/CustomDragPreview";
import { MoveShelf, type MovedShelf } from "./MoveShelf";
import { UpsertDeleteViewShelf } from "./UpsertDeleteViewShelf";

export const ShelvesTree = ({
	isLoading,
	searchShelves,
	error,
	setError,
	selectedNodes,
	setSelectedNodes,
}: {
	isLoading: boolean;
	searchShelves: Function;
	error: string;
	setError: (err: string) => void;
	selectedNodes: NodeModel<FileProperties>[];
	setSelectedNodes: (nodes: NodeModel<FileProperties>[]) => void;
}) => {
	const [showModal, setShowModal] = useState<
		"new" | "edit" | "delete" | "view" | "move" | ""
	>("");
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	// Destination picked by dropping a node on it; null when Move Shelf comes from the menu
	const [moveDestinationId, setMoveDestinationId] = useState<number | null>(
		null,
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const {
		state: { shelves, shelvesNodes, openShelves, shelfSelectedInTree },
		actions: { setShelfSelectedInTree, setOpenShelves },
	} = useShelvesContext();

	const { getMenuOptions } = useShelvesOptionsFunctions({
		setShelfNodeSelectedInTree: setShelfSelectedInTree,
		setShowModal,
	});

	const openShelvesArray = useMemo(() => {
		return Object.keys(openShelves).map((el) => parseInt(el));
	}, [openShelves]);

	const sortByDroppableName = (
		a: NodeModel<FileProperties>,
		b: NodeModel<FileProperties>,
	): number => {
		if (a.droppable && !b.droppable) return -1;
		if (!a.droppable && b.droppable) return 1;

		return (a.text ?? "").localeCompare(b.text ?? "");
	};

	const handleContextMenu = (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		if (e.type === "contextmenu") {
			setSelectedNodes([data]);
			showContextMenu(e, data, "shelvesContext");
			const selectedNode = shelves.find(
				(node: Shelf) => node.TreeId === data.id,
			);
			if (selectedNode) {
				setShelfSelectedInTree(selectedNode);
			}
		}
	};

	const handleSingleSelect = (node: NodeModel<FileProperties>) => {
		setSelectedNodes([node]);
	};

	const handleMultiSelect = (clickedNode: NodeModel<FileProperties>) => {
		const selectedIds = selectedNodes.map((n) => n.id);

		if (selectedIds.includes(clickedNode.id)) {
			return;
		}
		if (
			selectedIds.some((selectedId) =>
				isAncestor(shelvesNodes, selectedId, clickedNode.id),
			)
		) {
			return;
		}

		let updateNodes = [...selectedNodes];

		updateNodes = updateNodes.filter((selectedNode) => {
			return !isAncestor(shelvesNodes, clickedNode.id, selectedNode.id);
		});

		updateNodes = [...updateNodes, clickedNode];
		setSelectedNodes(updateNodes);
	};

	const handleCtrlClick = (
		e: ReactMouseEvent,
		node: NodeModel<FileProperties>,
	) => {
		if (e.ctrlKey || e.metaKey) {
			handleMultiSelect(node);
		} else {
			handleSingleSelect(node);
		}
	};

	const onNodeDoubleClicked = (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		const selectedNode = shelves.find((node: Shelf) => node.TreeId === data.id);
		if (selectedNode) {
			setShelfSelectedInTree(selectedNode);
		}
		setShowModal("view");
	};

	const handleHideModal = (shouldUpdate: boolean) => {
		setShowModal("");
		if (shouldUpdate) {
			searchShelves();
		}
	};

	const keyLength = (node: Shelf) => (node.UniqueKey ?? "").trim().length;

	// A shelf goes into any group, a group directly under any main branch, never where it already is.
	// Drawing numbers are built from the three codes, so there is no fourth level.
	const canMoveInto = (source: Shelf, destination: Shelf) =>
		!destination.isLeaf &&
		destination.TreeId !== source.TreeId &&
		destination.TreeId !== source.ParentTreeId &&
		(keyLength(source) === 3
			? destination.ParentTreeId === null && keyLength(destination) === 2
			: keyLength(destination) === 3);

	// Set while the dragged node holds documents: nothing accepts it and the preview says why
	const [documentsReason, setDocumentsReason] = useState<{
		treeId: number;
		reason: string;
	} | null>(null);
	const draggedIdRef = useRef<number | null>(null);

	const handleDragStart = (node: NodeModel<FileProperties>) => {
		const treeId = node.id as number;
		draggedIdRef.current = treeId;
		setDocumentsReason(null);
		moveShelf(treeId, 0, null, false)
			.then(({ summary }) => {
				if (draggedIdRef.current !== treeId || !summary?.Documents) return;
				setDocumentsReason({ treeId, reason: summary.Reason ?? "" });
			})
			.catch(() => {});
	};

	const handleDragEnd = () => {
		draggedIdRef.current = null;
		setDocumentsReason(null);
	};

	// Same handling as the Assets Tree: the Tree outlines the destination under the pointer and
	// calls onDrop with it. Dropping opens Move Shelf to confirm; it never moves anything by itself.
	const handleCanDrop = (
		_: NodeModel<FileProperties>[],
		{ dragSourceId, dropTargetId }: DropOptions<FileProperties>,
	) => {
		const source = shelves.find((node: Shelf) => node.TreeId === dragSourceId);
		const destination = shelves.find(
			(node: Shelf) => node.TreeId === dropTargetId,
		);
		return (
			!!source &&
			!!destination &&
			documentsReason?.treeId !== source.TreeId &&
			canMoveInto(source, destination)
		);
	};

	// Shown under the dragged chip: where it will go, or why it cannot go where the pointer is
	const dropFeedback = (
		monitorProps: DragLayerMonitorProps<FileProperties>,
	): DropFeedback => {
		const source = shelves.find(
			(node: Shelf) => node.TreeId === monitorProps.item.id,
		);
		if (!source) return null;
		if (documentsReason?.treeId === source.TreeId) {
			return { canDrop: false, text: documentsReason.reason };
		}
		const isGroup = keyLength(source) === 3;
		const wanted = isGroup ? "a main branch" : "a group";
		const offset = monitorProps.clientOffset;
		const rowId = offset
			? Number(
					document
						.elementFromPoint(offset.x, offset.y)
						?.closest("[data-node-id]")
						?.getAttribute("data-node-id"),
				)
			: 0;
		const row = shelves.find((node: Shelf) => node.TreeId === rowId);
		if (!row || row.TreeId === source.TreeId) {
			return { canDrop: false, text: `Drop it on ${wanted}` };
		}
		if (canMoveInto(source, row)) {
			return { canDrop: true, text: `Release to move it into ${row.Title}` };
		}
		if (row.TreeId === source.ParentTreeId) {
			return { canDrop: false, text: `It is already in ${row.Title}` };
		}
		const rowKind = row.isLeaf
			? "a shelf"
			: keyLength(row) === 2
				? "a main branch"
				: "a group";
		return {
			canDrop: false,
			text: `${isGroup ? "A group" : "A shelf"} goes into ${wanted}, not into ${rowKind}`,
		};
	};

	const handleDrop = (
		_: NodeModel<FileProperties>[],
		{ dragSourceId, dropTargetId }: DropOptions<FileProperties>,
	) => {
		const source = shelves.find((node: Shelf) => node.TreeId === dragSourceId);
		if (!source) return;
		setSelectedNodes(
			shelvesNodes.filter((node: NodeModel) => node.id === dragSourceId),
		);
		setShelfSelectedInTree(source);
		setMoveDestinationId(dropTargetId as number);
		setShowModal("move");
	};

	// Reload the tree with the moved node selected and its new branch open
	const handleMoveHidden = async (moved?: MovedShelf) => {
		setShowModal("");
		setMoveDestinationId(null);
		if (!moved) return;
		setNotificationMessage(moved.message);

		const newOpenShelves = { ...openShelves };
		let parentId: number | null = moved.newParentTreeId;
		while (parentId !== null) {
			newOpenShelves[parentId] = parentId;
			parentId =
				shelves.find((node: Shelf) => node.TreeId === parentId)?.ParentTreeId ??
				null;
		}
		setOpenShelves(newOpenShelves);
		setSelectedNodes([
			{ id: moved.treeId, parent: moved.newParentTreeId, text: "" },
		]);

		await searchShelves();
		setTimeout(() => {
			document
				.querySelector(`[data-node-id="${moved.treeId}"]`)
				?.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 300);
	};

	useEffect(() => {
		registerConfig({
			id: "shelvesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<HelmetDexteel title="Shelves Tree" />
			{isLoading ? (
				<div
					style={{
						display: "flex",
						minHeight: "300px",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<CircularProgress />
				</div>
			) : (
				<Grid2 container alignItems="center">
					<Box
						sx={{
							height: "60vh",
							width: "100%",
							overflowY: "auto",
							paddingBottom: 10,
							"& .root": {
								boxSizing: "border-box",
								height: "100%",
								py: 2,
								px: 4,
							},
							"& .dropTarget": {
								boxShadow: `0 0 0 1px ${mesfColors.info.dark} inset`,
							},
						}}
					>
						<Tree
							rootId={0}
							initialOpen={openShelvesArray}
							tree={shelvesNodes}
							sort={sortByDroppableName}
							insertDroppableFirst={false}
							enableAnimateExpand={true}
							classes={{
								root: "root",
								dropTarget: "dropTarget",
							}}
							canDrag={(node) =>
								!!node && !(node.parent === 0 && node.droppable)
							}
							canDrop={handleCanDrop}
							onDrop={handleDrop}
							dragPreviewRender={(
								monitorProps: DragLayerMonitorProps<FileProperties>,
							) => (
								<CustomDragPreview
									monitorProps={monitorProps}
									feedback={dropFeedback(monitorProps)}
								/>
							)}
							onDragStart={handleDragStart}
							onDragEnd={handleDragEnd}
							render={(node, options) => {
								const selected = selectedNodes.some(
									(selectedNode) => selectedNode.id === node.id,
								);
								return (
									<CustomNode
										setContextMenuOver={handleContextMenu}
										node={node as NodeModel<FileProperties>}
										{...options}
										isSelected={selected}
										isDragging={false}
										onClick={handleCtrlClick}
										onDoubleClick={onNodeDoubleClicked}
									/>
								);
							}}
						/>
					</Box>
				</Grid2>
			)}
			<UpsertDeleteViewShelf
				modal={showModal === "move" ? "" : showModal}
				onHide={handleHideModal}
			/>
			<MoveShelf
				shelf={showModal === "move" ? shelfSelectedInTree : null}
				initialDestinationId={moveDestinationId}
				shelves={shelves}
				onHide={handleMoveHidden}
			/>
			<Snackbar
				open={!!notificationMessage}
				autoHideDuration={6000}
				onClose={() => setNotificationMessage("")}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert onClose={() => setNotificationMessage("")} severity="success">
					{notificationMessage}
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
